import { useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router';
import { VAT_RATE_BY_TREATMENT, VAT_TREATMENTS, formatEur, type Customer, type InvoiceDetail, type VatTreatment } from '@aviaconsole/shared';
import { capi } from '../api/client';
import { useAuth } from '../auth/AuthProvider';
import { useI18n } from '../i18n';
import { useToast } from '../components/Toast';
import { Button, Card, Checkbox, Chip, Input, PageHeader, Select, vatTreatmentLabel } from '../components/ui';

/** What the API reads out of a PDF (services/pdfImport.ts). */
interface InvoiceGuess {
  number: string | null; issuedAt: string | null; dueAt: string | null; subject: string | null; reference: string | null;
  subtotalCents: number | null; vatCents: number | null; totalCents: number | null; vatRate: number | null; reverseCharge: boolean;
  customer: { id: string; name: string } | null; found: string[]; warnings: string[];
}

const MAX_FILES = 10;

/** One PDF on its way in: the file, what was read from it, the fields as edited, and how far the import got. */
interface Row {
  key: string; file: File; guess: InvoiceGuess | null; reading: boolean;
  customerId: string; number: string; issuedAt: string; dueAt: string; subject: string; reference: string;
  vat: VatTreatment | ''; subtotal: string; vatAmount: string; vatTouched: boolean; paid: boolean; paidAt: string;
  status: 'editing' | 'importing' | 'done' | 'failed'; result?: InvoiceDetail; error?: string;
}

function parseMoney(s: string): number { const n = Number(s.replace(/\./g, '').replace(',', '.')); return Number.isFinite(n) ? Math.round(n * 100) : 0; }
function money(c: number): string { return (c / 100).toFixed(2).replace('.', ','); }
const today = () => new Date().toISOString().slice(0, 10);

function newRow(file: File): Row {
  return {
    key: `${file.name}-${file.size}-${Math.random().toString(36).slice(2, 8)}`, file, guess: null, reading: true,
    customerId: '', number: '', issuedAt: '', dueAt: '', subject: '', reference: '', vat: '', subtotal: '', vatAmount: '', vatTouched: false, paid: false, paidAt: today(),
    status: 'editing',
  };
}

/** The form fields a guess fills in; what it did not find stays as it was. */
function applyGuess(row: Row, g: InvoiceGuess): Row {
  const vat: VatTreatment | '' = g.reverseCharge ? 'reverse_charge' : g.vatRate === 21 ? 'nl_21' : g.vatRate === 9 ? 'nl_9' : g.vatRate === 0 ? 'nl_0' : row.vat;
  return {
    ...row, guess: g, reading: false,
    number: g.number ?? row.number, issuedAt: g.issuedAt ?? row.issuedAt, dueAt: g.dueAt ?? row.dueAt, subject: g.subject ?? row.subject, reference: g.reference ?? row.reference,
    customerId: g.customer?.id ?? row.customerId, subtotal: g.subtotalCents !== null ? money(g.subtotalCents) : row.subtotal,
    vatAmount: g.vatCents !== null ? money(g.vatCents) : row.vatAmount, vatTouched: g.vatCents !== null || row.vatTouched, vat,
  };
}

/**
 * Brings invoices that were issued in an earlier tool into the console, up to
 * ten at a time: each PDF is read on arrival and its fields pre-filled, the
 * user checks the rows, and one click imports them all. A row that fails stays
 * editable while the others go through.
 */
export function InvoiceImport() {
  const { company } = useAuth();
  const { t } = useI18n();
  const qc = useQueryClient();
  const toast = useToast();
  const fileInput = useRef<HTMLInputElement>(null);
  const customers = useQuery({ queryKey: ['customers', company?.id], queryFn: () => capi<Customer[]>('/customers') });
  const [rows, setRows] = useState<Row[]>([]);
  const patch = (key: string, p: Partial<Row> | ((r: Row) => Row)) => setRows((rs) => rs.map((r) => (r.key === key ? (typeof p === 'function' ? p(r) : { ...r, ...p }) : r)));

  const inspect = async (row: Row) => {
    const fd = new FormData(); fd.append('file', row.file, row.file.name);
    try { const g = await capi<InvoiceGuess>('/invoices/import/inspect', { method: 'POST', body: fd }); patch(row.key, (r) => applyGuess(r, g)); }
    catch (e) { patch(row.key, { reading: false, guess: { number: null, issuedAt: null, dueAt: null, subject: null, reference: null, subtotalCents: null, vatCents: null, totalCents: null, vatRate: null, reverseCharge: false, customer: null, found: [], warnings: [] } }); toast.push(e instanceof Error ? e.message : String(e), 'error'); }
  };

  const add = (files: File[]) => {
    const room = MAX_FILES - rows.length;
    if (files.length > room) toast.push(t('importTooMany', { max: MAX_FILES }), 'error');
    const fresh = files.slice(0, Math.max(0, room)).filter((f) => f.type === 'application/pdf' || f.name.toLowerCase().endsWith('.pdf')).map(newRow);
    if (!fresh.length) return;
    setRows((rs) => [...rs, ...fresh]);
    fresh.forEach((r) => void inspect(r));
  };

  const importAll = useMutation({
    mutationFn: async () => {
      let ok = 0;
      // One at a time: each import renders an e-Factuur and talks to object storage.
      for (const row of rows.filter((r) => r.status === 'editing' || r.status === 'failed')) {
        patch(row.key, { status: 'importing', error: undefined });
        const fd = new FormData();
        fd.append('data', JSON.stringify({
          customerId: row.customerId, number: row.number.trim(), issuedAt: row.issuedAt, dueAt: row.dueAt || undefined, subject: row.subject || null, reference: row.reference || null,
          vatTreatment: row.vat || undefined, subtotalCents: parseMoney(row.subtotal), vatCents: vatCentsOf(row, customers.data), paidAt: row.paid ? row.paidAt : null,
        }));
        fd.append('file', row.file, row.file.name);
        try {
          const inv = await capi<InvoiceDetail>('/invoices/import', { method: 'POST', body: fd });
          patch(row.key, { status: 'done', result: inv }); ok++;
        } catch (e) {
          patch(row.key, { status: 'failed', error: e instanceof Error ? e.message : String(e) });
        }
      }
      return ok;
    },
    onSuccess: (ok) => { qc.invalidateQueries({ queryKey: ['invoices'] }); qc.invalidateQueries({ queryKey: ['dashboard'] }); if (ok) toast.push(t('importedCount', { count: ok })); },
  });

  const pending = rows.filter((r) => r.status === 'editing' || r.status === 'failed');
  const ready = pending.length > 0 && pending.every((r) => isReady(r)) && !rows.some((r) => r.reading);
  return (
    <>
      <PageHeader crumb={t('invDetailCrumb')} title={t('importInvoice')} sub={t('importInvoiceSub')}
        actions={<>
          <Button icon="fas fa-file-pdf" onClick={() => fileInput.current?.click()} disabled={rows.length >= MAX_FILES || importAll.isPending}>{t('choosePdfs', { max: MAX_FILES })}</Button>
          <Button variant="primary" icon="fas fa-file-import" disabled={!ready || importAll.isPending} onClick={() => importAll.mutate()}>{t('importAll', { count: pending.length })}</Button>
        </>} />
      <input ref={fileInput} type="file" accept="application/pdf" multiple hidden onChange={(e) => { add(Array.from(e.target.files ?? [])); e.target.value = ''; }} />
      <div className="stack">
        {rows.length === 0 && (
          <Card>
            <div className="stack md" style={{ alignItems: 'flex-start' }}>
              <Button variant="primary" icon="fas fa-file-pdf" onClick={() => fileInput.current?.click()}>{t('choosePdfs', { max: MAX_FILES })}</Button>
              <div className="hint">{t('importPdfHint')}</div>
              {customers.data && customers.data.length === 0 && <div className="small muted">{t('noCustomers')}</div>}
            </div>
          </Card>
        )}
        {rows.map((row, i) => (
          <ImportRow key={row.key} index={i} row={row} customers={customers.data ?? []} busy={importAll.isPending}
            onChange={(p) => patch(row.key, p)} onRemove={() => setRows((rs) => rs.filter((r) => r.key !== row.key))} />
        ))}
        {rows.length > 0 && <div className="hint">{t('importPdfHint')} {t('importCheckHint')}</div>}
      </div>
    </>
  );
}

function isReady(r: Row): boolean {
  return !!r.customerId && r.number.trim() !== '' && r.issuedAt !== '' && r.subtotal !== '' && (!r.paid || r.paidAt !== '');
}

function effectiveVat(r: Row, customers: Customer[] | undefined): VatTreatment {
  return (r.vat || customers?.find((c) => c.id === r.customerId)?.vatTreatment || 'nl_21') as VatTreatment;
}

/** The VAT follows the treatment until the user (or the PDF) gave an amount of its own. */
function vatCentsOf(r: Row, customers: Customer[] | undefined): number {
  const sub = parseMoney(r.subtotal);
  return r.vatTouched && r.vatAmount !== '' ? parseMoney(r.vatAmount) : Math.round(sub * VAT_RATE_BY_TREATMENT[effectiveVat(r, customers)] / 100);
}

const FIELD_KEYS = ['number', 'issuedAt', 'dueAt', 'customer', 'subject', 'reference', 'subtotal', 'vat', 'total'] as const;

function ImportRow({ index, row, customers, busy, onChange, onRemove }: { index: number; row: Row; customers: Customer[]; busy: boolean; onChange: (p: Partial<Row>) => void; onRemove: () => void }) {
  const { t, s } = useI18n();
  const customer = customers.find((c) => c.id === row.customerId);
  const vat = effectiveVat(row, customers);
  const rate = VAT_RATE_BY_TREATMENT[vat];
  const subtotalCents = parseMoney(row.subtotal);
  const vatCents = vatCentsOf(row, customers);
  const locked = row.status === 'done' || row.status === 'importing' || busy;
  const label = (k: string) => ({ number: t('invoiceNumber'), issuedAt: t('colIssued'), dueAt: t('colDue'), customer: t('customerLabel'), subject: t('subjectLine'), reference: t('reference'), subtotal: t('amountExclVat'), vat: t('vat'), total: t('totalDue') } as Record<string, string>)[k] ?? k;
  const g = row.guess;
  const missing = g ? FIELD_KEYS.filter((k) => !g.found.includes(k) && k !== 'subject' && k !== 'reference' && k !== 'total') : [];
  const customerOptions = [{ value: '', label: '—' }, ...customers.map((c) => ({ value: c.id, label: `${c.name} (${c.number})` }))];

  return (
    <Card tint={row.status === 'done' ? 'success' : row.status === 'failed' ? 'danger' : undefined}>
      <div className="row" style={{ marginBottom: '0.7rem', alignItems: 'center' }}>
        <Chip tone={row.status === 'done' ? 'success' : row.status === 'failed' ? 'danger' : 'neutral'}>{index + 1}</Chip>
        <i className="fas fa-file-pdf" aria-hidden="true" style={{ color: 'var(--danger)' }} />
        <span style={{ fontWeight: 600 }}>{row.file.name}</span>
        <span className="small muted">{(row.file.size / 1024).toFixed(0)} kB</span>
        {row.reading && <span className="small muted"><i className="fas fa-spinner fa-spin" aria-hidden="true" /> {t('importReading')}</span>}
        {row.status === 'importing' && <span className="small muted"><i className="fas fa-spinner fa-spin" aria-hidden="true" /> {t('importing')}</span>}
        {row.status === 'done' && row.result && <Link to={`/invoices/${row.result.id}`} className="small"><i className="fas fa-circle-check" aria-hidden="true" /> {t('importedAs', { number: row.result.number })}</Link>}
        {row.status === 'failed' && <span className="small" style={{ color: 'var(--danger)' }}><i className="fas fa-circle-exclamation" aria-hidden="true" /> {row.error}</span>}
        <div className="spacer" />
        {row.status !== 'importing' && <Button size="sm" icon={row.status === 'done' ? 'fas fa-xmark' : 'fas fa-trash-can'} onClick={onRemove} disabled={busy}>{row.status === 'done' ? t('dismiss') : t('remove')}</Button>}
      </div>
      {g && row.status !== 'done' && (
        g.found.length === 0
          ? <div className="banner tint-warning" style={{ marginBottom: '0.8rem' }}><i className="fas fa-triangle-exclamation" aria-hidden="true" /><span>{t('importNothingFound')}</span></div>
          : (missing.length > 0 || g.warnings.length > 0) && (
            <div className="banner tint-warning" style={{ marginBottom: '0.8rem', alignItems: 'flex-start' }}>
              <i className="fas fa-wand-magic-sparkles" aria-hidden="true" />
              <div className="stack" style={{ gap: '0.2rem' }}>
                {missing.length > 0 && <span>{t('importMissing', { fields: missing.map(label).join(', ') })}</span>}
                {g.warnings.includes('amounts') && <span className="small">{t('importAmountsOff')}</span>}
              </div>
            </div>
          )
      )}
      {row.status !== 'done' && (
        <div className="grid fields" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(170px, 1fr))' }}>
          <div style={{ gridColumn: 'span 2' }}><Select label={t('customerLabel')} value={row.customerId} options={customerOptions} disabled={locked} onChange={(e) => onChange({ customerId: e.target.value })} /></div>
          <Input label={t('invoiceNumber')} value={row.number} disabled={locked} onChange={(e) => onChange({ number: e.target.value })} />
          <Input label={t('colIssued')} type="date" value={row.issuedAt} disabled={locked} onChange={(e) => onChange({ issuedAt: e.target.value })} />
          <Input label={t('colDue')} type="date" value={row.dueAt} disabled={locked} onChange={(e) => onChange({ dueAt: e.target.value })} />
          <Select label={t('vatTreatment')} value={row.vat} disabled={locked} options={[{ value: '', label: `${s('Customer')} · ${customer ? s(vatTreatmentLabel(customer.vatTreatment)) : '—'}` }, ...VAT_TREATMENTS.map((v) => ({ value: v, label: s(vatTreatmentLabel(v)) }))]} onChange={(e) => onChange({ vat: e.target.value as VatTreatment | '' })} />
          <Input label={t('amountExclVat')} inputMode="decimal" placeholder="0,00" value={row.subtotal} disabled={locked} onChange={(e) => onChange({ subtotal: e.target.value })} />
          <Input label={`${t('vat')} ${rate}%`} inputMode="decimal" value={row.vatTouched ? row.vatAmount : money(vatCents)} disabled={locked} onFocus={() => { if (!row.vatTouched) onChange({ vatTouched: true, vatAmount: money(vatCents) }); }} onChange={(e) => onChange({ vatAmount: e.target.value })} />
          <div className="field"><div className="label">{t('totalDue')}</div><div className="value mono" style={{ fontWeight: 700 }}>{formatEur(subtotalCents + vatCents)}</div></div>
          <Input label={t('subjectLine')} value={row.subject} disabled={locked} onChange={(e) => onChange({ subject: e.target.value })} />
          <Input label={t('reference')} value={row.reference} disabled={locked} onChange={(e) => onChange({ reference: e.target.value })} />
          <div className="field" style={{ justifyContent: 'flex-end' }}><Checkbox label={t('importAlreadyPaid')} checked={row.paid} disabled={locked} onChange={(e) => onChange({ paid: e.target.checked })} /></div>
          {row.paid && <Input label={t('paymentDate')} type="date" value={row.paidAt} disabled={locked} onChange={(e) => onChange({ paidAt: e.target.value })} />}
        </div>
      )}
    </Card>
  );
}
